import { useEffect, useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { api, liveStreamAstrologerName, liveStreamElapsed } from '@astro/api'
import { appUrl } from '@astro/config'
import { LuArrowLeft, LuClock, LuTrendingUp, LuUserPlus, LuUsers } from 'react-icons/lu'
import PageLayout from '../components/layout/PageLayout'
import LiveKitStreamStage from '../components/sections/liveStream/LiveKitStreamStage'
import VideosdkStreamStage from '../components/sections/liveStream/VideosdkStreamStage'
import LiveStreamChat from '../components/sections/liveStream/LiveStreamChat'
import { useLiveStreamSession, viewerLabel } from '../components/sections/liveStream/liveStreamShared'

/**
 * One live session — the viewer's page and, for the astrologer who started it,
 * the broadcast studio. Both are this one page on purpose: the server decides
 * which by looking at who asked (POST /live-streams/:id/join answers with a
 * publisher payload for the host and a watch-only one for everybody else), so
 * there is no host URL to guess at and no client-side role to spoof.
 *
 * Open to everyone. A signed-out visitor gets the full picture and the full
 * chat history; the composer is the only thing they are asked to sign in for.
 *
 * Chrome: the app's own cream page shell (every other page here uses it), with
 * the video itself on black — that part is a picture, not a surface. There is
 * no live-sessions index to go back to; the band on the home page is where
 * these are found, so that is where the back link points.
 */
export default function LiveStreamPage() {
  const { id } = useParams()
  const [ending, setEnding] = useState(false)
  const { session, phase, error, isHost, isLive, viewerCount, chatEnabled, markEnded } = useLiveStreamSession(id)

  // The public card: the title and the astrologer, available before (and
  // after) a session exists to connect to — so an ended or not-yet-joined
  // stream still renders as something rather than a spinner.
  const card = useQuery({
    queryKey: ['live-stream', id],
    queryFn: () => api.liveStreams.get(id),
    retry: false,
    enabled: Boolean(id),
    // The heartbeat carries only the live figure (who is watching right now).
    // Peak and total joins live on the stream card, so while a broadcast runs
    // it is re-read - otherwise the host's counters would sit frozen at
    // whatever they were when they joined.
    refetchInterval: phase === 'ready' && isLive ? 20_000 : false,
  })
  // The card is the fresher of the two while live; the join's snapshot stands
  // in until the first card read lands.
  const stream = card.data?.data ?? session?.live_stream ?? null

  /* Once the broadcast is over the host has somewhere to be: their Go Live tab,
     where the session they just ran is already in the history and the start
     form is waiting for the next one. So they are sent straight there instead
     of being parked on an "it has ended" card about their own stream.

     Only the host. The dashboard is astrologer-only and most viewers are not
     signed in at all, so sending them would land them on a login wall - they
     keep the card, which is written for them anyway.

     `replace`, not `assign`: the finished stream should not sit in the history
     for the back button to return to. */
  const endedAsHost = phase === 'ended' && isHost
  useEffect(() => {
    if (!endedAsHost) return
    window.location.replace(appUrl('website', '/dashboard?tab=golive'))
  }, [endedAsHost])

  async function endBroadcast() {
    setEnding(true)
    try {
      await api.liveStreams.end(id)
    } catch {
      /* the stage is coming down either way; the server also reaps it */
    }
    markEnded()
    setEnding(false)
  }

  const Stage = session?.provider === 'livekit' ? LiveKitStreamStage : VideosdkStreamStage

  return (
    <PageLayout className="bg-[#403c3d]/5 text-[#151b21] antialiased min-h-screen flex flex-col">
      {/* The app header is `fixed top-0`, so every page pays for its own
          clearance - same offset the other inner pages use. Without it the
          title slides under the navbar. */}
      <main className="grow pt-28 lg:pt-32">
        {/* Bottom padding only. The top offset is `main`'s job (it clears the
            fixed header); a `py-*` here would stack on top of that and leave a
            dead band above the back link. */}
        <section className="mx-auto w-full max-w-350 px-4 pt-6 pb-8 sm:px-6 sm:pb-12 lg:px-12">
          <Link
            to="/"
            className="inline-flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-[.2em] transition-colors hover:opacity-70"
            style={{ color: '#8B7355' }}
          >
            <LuArrowLeft className="h-3.5 w-3.5" />
            Back to home
          </Link>

          {/* Title only. The live badge and the audience count used to take a
              line of their own above and a pill out to the right; both now ride
              on the picture itself (see the stage cell), which is where a
              viewer looks for them and which buys the stage its height back. */}
          <div className="mt-3 min-w-0">
            <h1
              className="font-cinzel text-2xl font-normal leading-[1.2] tracking-[.02em] sm:text-4xl"
              style={{ color: '#1C140A' }}
            >
              {stream?.title || 'Live session'}
            </h1>
            <p className="mt-2 text-sm font-light" style={{ color: '#8B7355' }}>
              with {liveStreamAstrologerName(stream)}
              {stream?.started_at && phase === 'ready' && ` · on for ${liveStreamElapsed(stream.started_at)}`}
              {isHost && ' · you are the host'}
            </p>
          </div>

          {/* Three cells, explicitly placed from lg up. The stage and the chat
              share row 1, so the chat stretches to exactly the stage's height -
              it used to be pinned at a flat 560px, which left it hanging well
              below the video. Everything that belongs under the stage sits in
              row 2 of the left column. On a phone there is no placement and the
              order-* classes stack them stage → details → chat. */}
          <div className="mt-6 grid gap-6 lg:grid-cols-[minmax(0,1fr)_340px] lg:items-stretch">
            {/* `relative` so the status chips can sit on the picture. For the
                host the stage renders the surface first and its controls under
                it, so the overlay's top-right is the video's top-right either
                way. */}
            <div className="relative order-1 min-w-0 lg:col-start-1 lg:row-start-1">
              {/* On the picture, not above it: glass chips over whatever is
                  playing, so they read on a bright frame as well as a dark one.
                  `pointer-events-none` keeps them from eating clicks meant for
                  the video. */}
              {phase === 'ready' && (
                <div className="pointer-events-none absolute right-3 top-3 z-10 flex items-center gap-2 sm:right-4 sm:top-4">
                  {isLive && (
                    <span
                      className="inline-flex items-center gap-1.5 rounded-full px-2.5 py-1.5 backdrop-blur-sm"
                      style={{ background: 'rgba(28,20,10,.62)', border: '1px solid rgba(255,255,255,.16)' }}
                    >
                      <span className="relative flex h-2 w-2">
                        <span className="absolute inline-flex h-full w-full rounded-full bg-red-500 opacity-70 animate-ping" />
                        <span className="relative inline-flex h-2 w-2 rounded-full bg-red-600" />
                      </span>
                      <span className="font-cinzel text-[9px] font-semibold uppercase tracking-[.2em] text-white">
                        Live
                      </span>
                    </span>
                  )}
                  <span
                    className="inline-flex items-center gap-1.5 rounded-full px-2.5 py-1.5 text-[11px] font-bold text-white backdrop-blur-sm"
                    style={{ background: 'rgba(28,20,10,.62)', border: '1px solid rgba(255,255,255,.16)' }}
                  >
                    <LuUsers className="h-3.5 w-3.5" style={{ color: 'rgba(200,146,42,.9)' }} />
                    {viewerLabel(viewerCount)}
                  </span>
                </div>
              )}

              {phase === 'connecting' && <Placeholder title="Connecting…" body="Setting up the session." />}

              {phase === 'missing' && (
                <Placeholder title="We could not find this session" body="The link may be wrong, or the session was removed." />
              )}

              {phase === 'error' && <Placeholder title="Something went wrong" body={error} />}

              {/* The host never reads this - the effect above is already
                  taking them to their dashboard - so they get a one-line
                  hand-off for the moment the navigation takes. */}
              {endedAsHost && (
                <Placeholder title="Your stream has ended" body="Taking you back to your dashboard…" />
              )}

              {phase === 'ended' && !isHost && (
                <Placeholder
                  title="This session has ended"
                  body="Live sessions are not recorded, so there is no replay. Head back for whoever is live now, or book a one-to-one consultation."
                  action={
                    <div className="mt-6 flex flex-wrap justify-center gap-3">
                      <Link
                        to="/"
                        className="rounded-full px-6 py-3 text-[11px] font-bold uppercase tracking-[.2em]"
                        style={{ background: '#C8922A', color: '#1C140A' }}
                      >
                        See who is live
                      </Link>
                      <Link
                        to="/consultations"
                        className="rounded-full px-6 py-3 text-[11px] font-bold uppercase tracking-[.2em] text-white"
                        style={{ border: '1px solid rgba(255,255,255,.25)' }}
                      >
                        Book a consultation
                      </Link>
                    </div>
                  }
                />
              )}

              {phase === 'ready' && session && (
                <Stage
                  session={session}
                  isHost={isHost}
                  onEnd={ending ? undefined : endBroadcast}
                  // The SFU dropping us is not the same as the stream ending —
                  // the heartbeat is what decides that — so a disconnect only
                  // matters for the host, who has left their own broadcast.
                  onLeft={isHost ? markEnded : undefined}
                />
              )}
            </div>

            {/* Row 2 of the left column: what reads *under* the stage. Kept out
                of the stage's cell so it cannot stretch the chat beside it. */}
            <div className="order-2 min-w-0 space-y-4 lg:col-start-1 lg:row-start-2">
              {isHost && phase === 'ready' && (
                <>
                  <HostStats
                    watching={viewerCount}
                    peak={stream?.peak_viewers}
                    total={stream?.total_viewers}
                    startedAt={stream?.started_at}
                    chatEnabled={chatEnabled}
                  />
                  <p className="text-xs font-light" style={{ color: '#8B7355' }}>
                    Your session shows on the home page while it runs. Viewers can watch without an account; only
                    signed-in viewers can chat.
                  </p>
                </>
              )}

              {stream?.description && (
                <div
                  className="rounded-3xl p-6"
                  style={{ background: '#FFF', border: '1px solid rgba(200,146,42,.18)' }}
                >
                  <h2 className="font-cinzel text-sm uppercase tracking-[.2em]" style={{ color: 'rgba(200,146,42,.85)' }}>
                    About this session
                  </h2>
                  <p className="mt-3 text-sm font-light leading-relaxed" style={{ color: '#6b6054' }}>
                    {stream.description}
                  </p>
                </div>
              )}
            </div>

            {/* The chat stays up for an ended session — the conversation is
                worth reading after the fact, and the server keeps serving it
                while refusing new messages. */}
            <div className="order-3 min-w-0 lg:col-start-2 lg:row-start-1">
              <LiveStreamChat streamId={id} enabled={phase === 'ready' || phase === 'ended'} chatEnabled={chatEnabled} />
            </div>
          </div>
        </section>
      </main>
    </PageLayout>
  )
}

/**
 * The host's own read-out while they are on air: who is watching this second,
 * the high-water mark, how many have come through in total, and how long they
 * have been running.
 *
 * Only the host sees this. A viewer has the audience pill at the top of the
 * page and no business with the rest - peak and total are the broadcaster's
 * numbers, not the audience's.
 *
 * "Watching now" comes from the heartbeat (every few seconds); peak and total
 * come from the stream card, re-read on a slower timer above. A dash rather
 * than a 0 while a figure has not arrived yet, so an empty room and an
 * unknown number never look the same.
 */
function HostStats({ watching, peak, total, startedAt, chatEnabled }) {
  const stat = (n) => (Number.isFinite(Number(n)) ? Number(n) : null)
  const items = [
    { icon: LuUsers, label: 'Watching now', value: stat(watching), accent: true },
    { icon: LuTrendingUp, label: 'Peak viewers', value: stat(peak) },
    { icon: LuUserPlus, label: 'Total joins', value: stat(total) },
  ]

  return (
    <div
      className="rounded-3xl p-4 sm:p-5"
      style={{ background: '#FFF', border: '1px solid rgba(200,146,42,.18)' }}
    >
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h2 className="font-cinzel text-sm uppercase tracking-[.2em]" style={{ color: 'rgba(200,146,42,.85)' }}>
          Your audience
        </h2>
        <span className="inline-flex items-center gap-1.5 text-[11px] font-light" style={{ color: '#8B7355' }}>
          <LuClock className="h-3.5 w-3.5" />
          {startedAt ? `On air ${liveStreamElapsed(startedAt)}` : 'On air'}
          <span aria-hidden="true">·</span>
          {chatEnabled ? 'Chat on' : 'Chat off'}
        </span>
      </div>

      <dl className="mt-4 grid grid-cols-3 gap-3">
        {items.map(({ icon: Icon, label, value, accent }) => (
          <div
            key={label}
            className="rounded-2xl px-3 py-3 text-center sm:px-4"
            style={
              accent
                ? { background: 'rgba(200,146,42,.08)', border: '1px solid rgba(200,146,42,.25)' }
                : { background: 'rgba(28,20,10,.03)', border: '1px solid rgba(28,20,10,.06)' }
            }
          >
            <dt
              className="flex items-center justify-center gap-1.5 text-[10px] font-semibold uppercase tracking-[.12em]"
              style={{ color: '#8B7355' }}
            >
              <Icon className="h-3.5 w-3.5" />
              <span className="hidden sm:inline">{label}</span>
            </dt>
            <dd className="mt-1.5 font-cinzel text-2xl leading-none" style={{ color: '#1C140A' }}>
              {value === null ? '—' : value}
            </dd>
            <dd className="mt-1 text-[10px] font-light sm:hidden" style={{ color: '#8B7355' }}>
              {label}
            </dd>
          </div>
        ))}
      </dl>
    </div>
  )
}

/** The stage's own frame, for every state that has no picture in it yet. Black,
 *  like the video it stands in for, so the layout doesn't shift when one
 *  replaces the other. */
function Placeholder({ title, body, action = null }) {
  return (
    <div
      className="flex aspect-video w-full flex-col items-center justify-center rounded-3xl px-6 text-center"
      style={{ background: 'linear-gradient(135deg,#2A1F14,#120F0C)', border: '1px solid rgba(200,146,42,.18)' }}
    >
      <p className="font-cinzel text-lg text-white sm:text-xl">{title}</p>
      {body && <p className="mt-2 max-w-md text-xs font-light leading-relaxed text-white/50 sm:text-sm">{body}</p>}
      {action}
    </div>
  )
}
